import React from 'react'
import {Link} from 'gatsby'

import Layout from '../components/layouts/layout'
import Tooltip from '../components/tooltip'
import SocialProofSection from '../components/social-proof-section'
import checkMark from '../../img/black-checkmark.svg'
import tagIcon from '../../img/tag-icon.svg'
import infoIcon from '../../img/info-icon.svg'

import styles from './pricing.module.scss'
import {combine} from '../styles/classname-utils'
import BasicPageHeading from '../components/basic-page-heading'
import PricingFaqSection from '../components/pricing-faq-section'
import PricingTable from '../components/pricing-table'

const IncludedFeature = ({className = '', info, style = {}}) => (
  <div>
    <img
      src={checkMark}
      className={combine('img-fluid', 'img-responsive', styles.checkMark, 'align-middle')}
      alt=''
    />
    <span
      style={style}
      className={combine(
        'text8-md',
        'align-middle',
        className,
        styles.includedFeatureInfo
      )}
    >
      {info}
    </span>
  </div>
)

export default () => {
  const tryFreeTrial = (accountType: string) => () => {
    sessionStorage.setItem('free-trial.plan', accountType)
    window.location.href = '/try-free-trial'
  }
  const handleContactLicensing = () => {
    window.location.href = '/licensing'
  }
  return (
    <Layout
      title='Pricing and Plans - WebAR, AR, WebVR Developer Tools and SDK'
      description='Compare plans and get started with a 14-day free trial of 8th Wall so you and
        your team can create, collaborate and publish extraordinary WebAR content.'
    >
      <BasicPageHeading>
        Pricing
      </BasicPageHeading>
      <section className='light'>
        <div className='row m-0 justify-content-center' id='pricing-container'>
          <div className='col-12 col-md-10 col-lg-8 max-width text-center'>
            <h2>Plans</h2>
            <p className='d-md-block d-none text8-lg noto-sans-jp font8-medium'>
              Get access to 8th Wall's powerful suite of developer tools to build extraordinary
              World Tracking, Image Target and Face Effect projects that work in a web browser — no
              app needed.
            </p>
          </div>
        </div>
        <div className='row m-0 justify-content-center'>
          <div className={`p-0 col-md-10 col-12 ${styles.planCard}`}>
            <div className={`card-body ${styles.planCardBody}`}>
              <h2 className={`card-title pt-2 ${styles.planName}`}>
                <span className={styles.name}>PRO</span>
                <Tooltip
                  text='Ideal for agencies working on client projects or developers
                  looking to get started with WebAR.'
                >
                  <img src={infoIcon} className={styles.icon} alt='' />
                </Tooltip>
              </h2>
              <p className='text8-md font8-bold'>
                The complete solution for you and your team to create extraordinary WebAR
                projects
              </p>
              <div className={styles.planPricingContainer}>
                <p className={styles.value}>$99</p>
                <p className='mb-0 text8-md'>/ month for your entire team</p>
              </div>
              <div className={styles.planButtonContainer}>
                <button
                  id='start-trial-agency'
                  type='button'
                  onClick={tryFreeTrial('WebAgency')}
                  className={
                    combine(styles.planButton, styles.trialButton)
                  }
                  a8='click;click-free-trial-cta;agency-free-trial-cta'
                >
                  Start a 14-day free trial
                </button>
              </div>
              <div className={styles.planButtonTooltip}>
                <div style={{marginRight: '0.5em'}}>
                  <img src={tagIcon} alt='tagIcon' />
                </div>
                <div>
                  Commercial Licenses purchased separately.
                  <br />
                  <a
                    className={
                      combine('mb-5', 'font8-bold', styles.underline)
                    }
                    href='#commercial-license'
                  >
                    View pricing
                  </a>
                </div>
              </div>
            </div>
            <div className={`card-footer ${styles.planFeatureList}`}>
              <IncludedFeature info='Unlimited seats' />
              <IncludedFeature info='AR Engine' />
              <IncludedFeature info='Cloud-based code editor' />
              <IncludedFeature info='WebAR project templates' />
              <IncludedFeature info='Free hosting on 8th Wall' />
              <IncludedFeature info='Ability to self-host' />
              <IncludedFeature info='Publish non-commercial projects' />
            </div>
          </div>
          <div className={`p-0 col-md-8 col-12 ${styles.planCard}`}>
            <div className={`card-body ${styles.planCardBody}`}>
              <h2 className={`card-title pt-2 ${styles.planName}`}>
                <span className={styles.name}>ENTERPRISE</span>
                <Tooltip
                  className={styles.enterpriseTooltip}
                  text='Ideal for organizations launching multiple or long-term WebAR projects,
                  or businesses looking to integrate 8th Wall technology into their website or
                  platform.'
                >
                  <img src={infoIcon} className={styles.icon} alt='' />
                </Tooltip>
              </h2>
              <p className='text8-md font8-bold'>
                A customized plan tailored for your company's success
              </p>
              <div className={styles.planPricingContainer}>
                <p className='my-2 text8-md'>
                  Contact sales for pricing and custom contracts
                </p>
              </div>
              <div className={styles.planButtonContainer}>
                <button
                  type='button'
                  id='start-trial-business'
                  onClick={handleContactLicensing}
                  className={
                    combine('light', styles.planButton)
                  }
                  a8='click;click-free-trial-cta;enterprise-contact-sales-cta'
                >
                  Contact Sales
                </button>
              </div>
              <div className={styles.planButtonTooltip}>
                Commercial Licenses included in contract
              </div>
            </div>
            <div className={`card-footer ${styles.planFeatureList}`}>
              <IncludedFeature
                className={styles.planEnterpriseFirst}
                info='Everything in Pro Plan'
              />
              <p className={combine(styles.plus, 'font8-bold', 'text8-md', 'my-2')}>
                PLUS:
              </p>
              <IncludedFeature info='Custom contracts and invoicing' />
              <IncludedFeature info='Volume pricing' />
              <IncludedFeature info='Long-term commercial licenses' />
              <IncludedFeature info='Access to exclusive platform APIs' />
              <IncludedFeature info='Priority technical support' />
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className='row m-0 justify-content-center'>
          <div className='col-12 col-md-10 col-lg-8 px-0 max-width text-center'>
            <h2 id='commercial-license'>
              Commercial Licenses
            </h2>
            <p className='noto-sans-jp text8-lg font8-medium mb-5'>
              Commercial projects require the purchase of a Commercial License prior to launching.
              When you’re ready to launch, select from one of the following monthly licenses which
              vary based on views.
            </p>
          </div>
        </div>
        <div className='row m-0 justify-content-center'>
          <div className={`col-md-9 col-12 mb-5 ${styles.licenseTableContainer}`}>
            <PricingTable />
          </div>
        </div>
        <div className='row m-0 justify-content-center'>
          <div className='col-12 col-md-10 col-lg-8 px-0 max-width'>
            <p className='text-center font8-medium text8-lg noto-sans-jp'>
              For custom commercial agreements and pricing,{' '}
              <Link
                to='/licensing'
                a8='click;purchase-intent;contact-licensing'
                style={{
                  fontWeight: 700,
                }}
              >
                contact&nbsp;Sales.
              </Link>{' '}
              <br />
              All commercial projects must display the{' '}
              <Link
                to='https://drive.google.com/drive/folders/1c9d23c5hS_HspHTUD7VceV6ocqdbPN7J?usp=sharing'
                a8='click;explore;download-powered-by-8thwall'
                target='_blank'
                rel='noopener noreferrer'
                style={{
                  fontWeight: 700,
                  color: '#2D2E43',
                }}
              >
                Powered&nbsp;by&nbsp;8th&nbsp;Wall&nbsp;badge
              </Link>
              {' '}on the loading page.
            </p>
          </div>
        </div>
      </section>

      <SocialProofSection a8='click;click-free-trial-cta;social-proof-cta' />

      <PricingFaqSection id='faq-section' />
    </Layout>
  )
}
